import { HttpApiMiddleware, HttpApiSchema } from "@effect/platform";
import { CurrentPrincipal } from "@pima/domain-data/principal-context";
import * as S from "effect/Schema";
import { ContractTypeSchema } from "./column-schemas.js";
export { CurrentPrincipal };
/**
 * The roles a signed-in person can hold, as the web read API authorizes
 * against them. The apps/api bridge derives it from the session's role UUID
 * (env `*_ROLE_ID` mapping); "other" carries no read grants anywhere,
 * matching a role with no yp_permissions row.
 */
export const RoleKind = S.Literal("ohs-admin", "sifa", "ama", "external-sifa", "ohs-officer", "location-manager", "employee", "other");
/**
 * The per-request identity every loader predicate keys on. Built by the
 * apps/api bridge from the session (`req.accountability`) plus one yp_users
 * read and the scope lookups (clients.sifa_id, location_shares).
 */
const principalFields = {
    userId: S.UUID,
    roleKind: RoleKind,
    /** Clients granted to an external SiFa (clients.sifa_id = user). */
    assignedClientIds: S.Array(S.UUID),
    /** yp_users.contract_types — narrows internal-role visibility. */
    contractTypes: S.Array(ContractTypeSchema),
    /** location_shares scope for location managers. */
    locationIds: S.Array(S.UUID),
    clientAccessScopes: S.optionalWith(S.Array(S.Struct({
        clientId: S.UUID,
        locationIds: S.NullOr(S.Array(S.UUID)),
    })), { default: () => [] }),
};
const PlatformAdminPrincipal = S.Struct({
    ...principalFields,
    isAdmin: S.Literal(true),
    clientId: S.Null,
});
const NonAdminPrincipal = S.Struct({
    ...principalFields,
    isAdmin: S.Literal(false),
    clientId: S.NullOr(S.UUID),
});
const PrincipalSchema = S.Union(PlatformAdminPrincipal, NonAdminPrincipal);
/** Platform admins carry no customer membership, independently of role kind. */
export const Principal = Object.assign(PrincipalSchema, {
    make: (props) => props.isAdmin
        ? PlatformAdminPrincipal.make(props)
        : NonAdminPrincipal.make(props),
});
export const Unauthorized = S.Struct({
    _tag: S.Literal("Unauthorized"),
}).annotations(HttpApiSchema.annotations({ status: 401 }));
/**
 * Group middleware providing CurrentPrincipal to every handler. Because it
 * `provides`, the derived client (@pima/web-api/client) never sees it — no
 * browser call site supplies a principal.
 */
export class PrincipalMiddleware extends HttpApiMiddleware.Tag()("@pima/web-api/PrincipalMiddleware", {
    provides: CurrentPrincipal,
    failure: Unauthorized,
}) {
}
/**
 * Internal header the Express bridge uses to thread the request principal
 * into the web handler (`toWebHandler` exposes no per-request context). The
 * bridge always overwrites it on the synthetic Request it builds, so an
 * external caller can never smuggle one in.
 */
export const PRINCIPAL_HEADER = "x-pima-principal";
const PrincipalFromJson = S.parseJson(Principal);
export const encodePrincipalHeader = (principal) => encodeURIComponent(S.encodeSync(PrincipalFromJson)(principal));
export const decodePrincipalHeader = (value) => S.decodeUnknownSync(PrincipalFromJson)(decodeURIComponent(value));
//# sourceMappingURL=principal.js.map